import React, { useEffect, useState } from 'react';
import { firestore } from '../firebase';
import AddSchedule from './AddSchedule';
import { PencilSquareIcon, TrashIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import PermissionGate from './PermissionGate';

function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [devices, setDevices] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to schedules
    const unsubscribeSchedules = firestore.collection('schedules')
      .orderBy('createdAt', 'desc')
      .onSnapshot(snapshot => {
        const scheduleList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSchedules(scheduleList);
        setLoading(false);
      });

    // Subscribe to devices
    const unsubscribeDevices = firestore.collection('cafeTVs').onSnapshot(snapshot => {
      const deviceList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDevices(deviceList);
    });

    // Subscribe to menu items
    const unsubscribeMenuItems = firestore.collection('menuItems').onSnapshot(snapshot => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMenuItems(items);
    });

    return () => {
      unsubscribeSchedules();
      unsubscribeDevices();
      unsubscribeMenuItems();
    };
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      await firestore.collection('schedules').doc(id).delete();
    }
  };

  const handleEdit = (schedule) => {
    setCurrentSchedule(schedule);
    setShowAdd(true);
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find(d => d.deviceId === deviceId);
    return device ? `${device.deviceName} (${device.storeCode})` : deviceId;
  };

  const getMenuTitle = (menuItemId) => {
    const menu = menuItems.find(m => m.id === menuItemId);
    return menu ? menu.title : 'Unknown Menu';
  };

  const formatDays = (days) => {
    if (!days || days.length === 0) return 'Every day';
    if (days.length === 7) return 'Every day';
    return days.join(', ');
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isFixedSchedule = (schedule) => {
    return schedule.startDateTime && schedule.endDateTime;
  };

  return (
    <div className="m-auto p-4 md:p-10 max-w-screen-xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl md:text-2xl font-bold">Menu Schedules</h2>
        <PermissionGate permission="tv.schedules.create"><button
          onClick={() => {
            setCurrentSchedule(null);
            setShowAdd(true);
          }}
          className="bg-teal-500 text-white px-3 py-2 md:px-4 rounded text-sm md:text-base"
        >
          Add Schedule
        </button></PermissionGate>
      </div>

      <div className="mb-6 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-xs md:text-sm text-gray-700">
          <strong>How schedules work:</strong> At the scheduled start time, the device will automatically switch
          to the scheduled menu. At the end time, it will revert back to the original menu. The backend function
          checks every minute for active schedules.
        </p>
      </div>

      {showAdd && (
        <AddSchedule
          onClose={() => setShowAdd(false)}
          devices={devices}
          menuItems={menuItems}
          currentSchedule={currentSchedule}
        />
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading schedules...</p>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-4">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className={`bg-white shadow rounded-lg p-4 ${schedule.isActive ? 'border-l-4 border-green-500' : ''}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {getDeviceName(schedule.deviceId)}
                    </h3>
                    <p className="text-teal-600 text-sm mt-1">
                      {getMenuTitle(schedule.scheduledMenuItemId)}
                    </p>
                  </div>
                  {schedule.isActive ? (
                    <span className="flex items-center gap-1 text-green-600 text-sm bg-green-100 px-2 py-1 rounded">
                      <CheckCircleIcon className="h-4 w-4" />
                      Active
                    </span>
                  ) : (
                    <span className="text-gray-500 text-sm bg-gray-100 px-2 py-1 rounded">Inactive</span>
                  )}
                </div>

                {isFixedSchedule(schedule) ? (
                  <div className="text-sm text-gray-600 mb-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      <span className="font-medium">One-time</span>
                    </div>
                    <div className="ml-5">
                      <div>{formatDateTime(schedule.startDateTime)}</div>
                      <div className="text-gray-400">to</div>
                      <div>{formatDateTime(schedule.endDateTime)}</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{schedule.startTime} - {schedule.endTime}</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      <span className="font-medium">Days:</span> {formatDays(schedule.days)}
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  <PermissionGate permission="tv.schedules.update"><button
                    onClick={() => handleEdit(schedule)}
                    className="text-teal-500 p-2 hover:bg-teal-50 rounded"
                    title="Edit schedule"
                  >
                    <PencilSquareIcon className="h-6 w-6" />
                  </button></PermissionGate>
                  <PermissionGate permission="tv.schedules.delete"><button
                    onClick={() => handleDelete(schedule.id)}
                    className="text-red-500 p-2 hover:bg-red-50 rounded"
                    title="Delete schedule"
                  >
                    <TrashIcon className="h-6 w-6" />
                  </button></PermissionGate>
                </div>
              </div>
            ))}
            {schedules.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No schedules found. Create your first schedule to automatically switch menus at specific times.
              </div>
            )}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded text-left">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Device</th>
                  <th className="py-2 px-4 border-b">Scheduled Menu</th>
                  <th className="py-2 px-4 border-b">Type</th>
                  <th className="py-2 px-4 border-b">Time / Date Range</th>
                  <th className="py-2 px-4 border-b">Status</th>
                  <th className="py-2 px-4 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id} className={schedule.isActive ? 'bg-green-50' : ''}>
                    <td className="py-2 px-4 border-b">
                      {getDeviceName(schedule.deviceId)}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {getMenuTitle(schedule.scheduledMenuItemId)}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {isFixedSchedule(schedule) ? (
                        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-sm">One-time</span>
                      ) : (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">Recurring</span>
                      )}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {isFixedSchedule(schedule) ? (
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4 text-gray-500" />
                            <span>{formatDateTime(schedule.startDateTime)}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-gray-400 ml-5">to</span>
                            <span>{formatDateTime(schedule.endDateTime)}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4 text-gray-500" />
                            <span>{schedule.startTime} - {schedule.endTime}</span>
                          </div>
                          <div className="text-gray-500 mt-1">{formatDays(schedule.days)}</div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {schedule.isActive ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircleIcon className="h-5 w-5" />
                          Active
                        </span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="py-2 px-4 border-b">
                      <PermissionGate permission="tv.schedules.update"><button
                        onClick={() => handleEdit(schedule)}
                        className="text-teal-500 mr-2 hover:opacity-70"
                        title="Edit schedule"
                      >
                        <PencilSquareIcon className="size-8" />
                      </button></PermissionGate>
                      <PermissionGate permission="tv.schedules.delete"><button
                        onClick={() => handleDelete(schedule.id)}
                        className="text-red-500 hover:opacity-70"
                        title="Delete schedule"
                      >
                        <TrashIcon className="size-8" />
                      </button></PermissionGate>
                    </td>
                  </tr>
                ))}
                {schedules.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No schedules found. Create your first schedule to automatically switch menus at specific times.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Schedules;
